import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Store, Search, Plus, Edit, Trash2, MapPin, Phone, Star, Truck, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';

export default function RestaurantManagement() {
  const { api } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editRestaurant, setEditRestaurant] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    cuisine_type: '',
    // Delivery settings (configuration, not daily toggle)
    delivery_radius_km: 10,
    minimum_order: 10,
    delivery_fee: 2.50,
    image_url: '',
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      city: '',
      postal_code: '',
      phone: '',
      email: '',
      cuisine_type: '',
      delivery_enabled: true,
      delivery_radius_km: 10,
      minimum_order: 10,
      delivery_fee: 2.50,
      image_url: '',
    });
    setEditRestaurant(null);
    setIsAddOpen(false);
  };

  const openEdit = (restaurant) => {
    setFormData({
      name: restaurant.name || '',
      description: restaurant.description || '',
      address: restaurant.address || '',
      city: restaurant.city || '',
      postal_code: restaurant.postal_code || '',
      phone: restaurant.phone || '',
      email: restaurant.email || '',
      cuisine_type: (restaurant.cuisine_type || []).join(', '),
      delivery_enabled: restaurant.delivery_enabled !== false,
      delivery_radius_km: restaurant.delivery_radius_km || 10,
      minimum_order: restaurant.minimum_order || 10,
      delivery_fee: restaurant.delivery_fee || 2.50,
      image_url: restaurant.image_url || '',
    });
    setEditRestaurant(restaurant);
    setIsAddOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        cuisine_type: formData.cuisine_type.split(',').map(s => s.trim()).filter(Boolean),
        delivery_radius_km: parseFloat(formData.delivery_radius_km),
        minimum_order: parseFloat(formData.minimum_order),
        delivery_fee: parseFloat(formData.delivery_fee),
      };

      if (editRestaurant) {
        await api.put(`/restaurants/${editRestaurant.id}`, data);
        toast.success('Restaurant updated');
      } else {
        await api.post('/restaurants', data);
        toast.success('Restaurant added');
      }

      fetchRestaurants();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save restaurant');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;
    
    try {
      await api.delete(`/restaurants/${id}`);
      toast.success('Restaurant deleted');
      fetchRestaurants();
    } catch (error) {
      toast.error('Failed to delete restaurant');
    }
  };

  const filteredRestaurants = restaurants.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.cuisine_type?.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Restaurant Management</h1>
        <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-restaurant-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Restaurant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editRestaurant ? 'Edit Restaurant' : 'Add New Restaurant'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Restaurant Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="restaurant-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="cuisine_type">Cuisine Types</Label>
                  <Input
                    id="cuisine_type"
                    name="cuisine_type"
                    value={formData.cuisine_type}
                    onChange={handleChange}
                    placeholder="Italian, Pizza"
                  />
                </div>
              </div>

              {/* Delivery Settings - Always shown, these are configuration not daily toggles */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Delivery Settings
                </h4>
                <p className="text-sm text-slate-500 mb-4">
                  Configure delivery parameters. The restaurant can enable/disable delivery daily from their dashboard.
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="delivery_radius_km">Delivery Radius (km)</Label>
                    <Input
                      id="delivery_radius_km"
                      name="delivery_radius_km"
                      type="number"
                      step="0.5"
                      value={formData.delivery_radius_km}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="minimum_order">Minimum Order (£)</Label>
                    <Input
                      id="minimum_order"
                      name="minimum_order"
                      type="number"
                      step="0.50"
                      value={formData.minimum_order}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="delivery_fee">Delivery Fee (£)</Label>
                    <Input
                      id="delivery_fee"
                      name="delivery_fee"
                      type="number"
                      step="0.10"
                      value={formData.delivery_fee}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" className="btn-primary" data-testid="save-restaurant-btn">
                  {editRestaurant ? 'Update' : 'Add'} Restaurant
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search restaurants..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-restaurants"
        />
      </div>

      {/* Restaurant List */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="h-40 bg-slate-200 rounded-lg mb-4"></div>
              <div className="h-6 bg-slate-200 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : filteredRestaurants.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRestaurants.map((restaurant) => (
            <Card key={restaurant.id} className="overflow-hidden" data-testid={`restaurant-${restaurant.id}`}>
              <div className="relative h-40 bg-gradient-to-br from-orange-400 to-orange-600">
                {restaurant.image_url ? (
                  <img 
                    src={restaurant.image_url} 
                    alt={restaurant.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store className="w-16 h-16 text-white/50" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button
                    size="icon"
                    variant="secondary"
                    className="w-8 h-8 bg-white/90"
                    onClick={() => openEdit(restaurant)}
                    data-testid={`edit-restaurant-${restaurant.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="w-8 h-8 bg-white/90 text-red-500"
                    onClick={() => handleDelete(restaurant.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {/* Service Status Badges */}
                <div className="absolute bottom-2 left-2 flex gap-1">
                  {restaurant.delivery_enabled && (
                    <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Delivery
                    </div>
                  )}
                  {restaurant.takeaway_enabled && (
                    <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      Takeaway
                    </div>
                  )}
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg text-slate-900 mb-1">{restaurant.name}</h3>
                <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                  <MapPin className="w-4 h-4" />
                  {restaurant.city}
                </div>
                {restaurant.cuisine_type?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {restaurant.cuisine_type.slice(0, 3).map((cuisine, idx) => (
                      <span key={idx} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                        {cuisine}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    Min: £{(restaurant.minimum_order || 10).toFixed(2)}
                  </span>
                  <span className="text-slate-500">
                    Fee: £{(restaurant.delivery_fee || 2.50).toFixed(2)}
                  </span>
                </div>
                {/* Order Stats */}
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-slate-500">
                  <span>{restaurant.delivery_order_count || 0} deliveries</span>
                  <span>{restaurant.takeaway_order_count || 0} takeaways</span>
                  <span>{restaurant.table_booking_count || 0} bookings</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl">
          <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No restaurants found</h2>
          <p className="text-slate-500 mb-6">Add your first restaurant to get started</p>
          <Button className="btn-primary" onClick={() => setIsAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Restaurant
          </Button>
        </div>
      )}
    </div>
  );
}
